import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as apiWarehouse from 'api/apiWarehouses';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import type { StockRow } from '@econtractor/types';
import { useWarehouse } from '../context/WarehouseProvider';
import { exportStocksToExcel } from './exportStocksToExcel';

export const useExportStocks = (wId: number) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { g } = useGlobal();
    const location = useLocation();
    const { stockFilter, stocksOrder, stocksDesc } = useWarehouse();
    const [exporting, setExporting] = useState(false);

    const exportStocks = useCallback(async () => {
        if (!wId || exporting) return;

        setExporting(true);
        try {
            const search = `%${g?.search?.[location.pathname] || ''}%`;
            const resStock = await apiWarehouse.getStocks(wId, {
                search,
                page: 1,
                onPage: 999999,
                order: `${stocksOrder ? `order by ${stocksOrder} ` : ''} ${stocksDesc ? 'DESC' : ''}`,
                ...stockFilter,
            });

            if (!resStock) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            const stocks = (resStock.stocks || []) as StockRow[];
            if (!stocks.length) {
                showToast(t('messExportEmpty'), 'warning');
                return;
            }

            await exportStocksToExcel(stocks, wId, {
                prodName: t('fields.prodName'),
                category: t('fields.category'),
                article: t('fields.article'),
                vendor: t('filter.labelVendors'),
                countEnd: t('fields.countEnd'),
                updated: t('fields.updated'),
            });
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    }, [
        wId,
        exporting,
        g?.search,
        location.pathname,
        stocksOrder,
        stocksDesc,
        stockFilter,
        showToast,
        t,
    ]);

    return { exportStocks, exporting };
};
